import React, { useState, useEffect } from 'react';

const UserModal = ({ isOpen, onClose, onSave, user = null, title, organizations = [] }) => {
  const [formData, setFormData] = useState({
    user_name: '',
    user_full_name: '', // New field for full name
    email: '',
    phone_numebr: '', // Note: keeping the typo to match backend
    user_type: '',
    organization_id: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        user_name: user.detail?.user_name || '',
        user_full_name: user.detail?.user_full_name || '',
        email: user.detail?.email || '',
        phone_numebr: user.detail?.phone_numebr || '',
        user_type: user.detail?.user_type || '',
        organization_id: user.organization_id || '',
        password: ''
      });
    } else {
      setFormData({
        user_name: '',
        user_full_name: '',
        email: '',
        phone_numebr: '',
        user_type: '',
        organization_id: '',
        password: ''
      });
      setGeneratedPassword('');
    }
    setErrors({});
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setGeneratedPassword(password);
    setFormData(prev => ({ ...prev, password }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.user_name.trim()) {
      newErrors.user_name = 'User name is required';
    }
    
    if (!formData.user_full_name.trim()) {
      newErrors.user_full_name = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone_numebr.trim()) {
      newErrors.phone_numebr = 'Phone number is required';
    } else if (!isValidPhone(formData.phone_numebr)) {
      newErrors.phone_numebr = 'Please enter a valid phone number';
    }
    
    if (!formData.user_type) {
      newErrors.user_type = 'User type is required';
    }
    
    if (!formData.organization_id) {
      newErrors.organization_id = 'Organization is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      setErrors({ submit: 'Failed to save user. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title || 'User'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {errors.submit && (
                  <div className="alert alert-danger" role="alert">
                    {errors.submit}
                  </div>
                )}
                
                {/* Row 1: User Name and Full Name */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        User Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.user_name ? 'is-invalid' : ''}`}
                        name="user_name"
                        value={formData.user_name}
                        onChange={handleChange}
                        placeholder="Username for login"
                        required
                      />
                      {errors.user_name && (
                        <div className="invalid-feedback">
                          {errors.user_name}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.user_full_name ? 'is-invalid' : ''}`}
                        name="user_full_name"
                        value={formData.user_full_name}
                        onChange={handleChange}
                        placeholder="User's full display name"
                        required
                      />
                      {errors.user_full_name && (
                        <div className="invalid-feedback">
                          {errors.user_full_name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Row 2: Email and Phone */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="email" 
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                      {errors.email && (
                        <div className="invalid-feedback">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="tel" 
                        className={`form-control ${errors.phone_numebr ? 'is-invalid' : ''}`}
                        name="phone_numebr"
                        value={formData.phone_numebr}
                        onChange={handleChange}
                        required
                      />
                      {errors.phone_numebr && (
                        <div className="invalid-feedback">
                          {errors.phone_numebr}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Row 3: User Type and Organization */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        User Type <span className="text-danger">*</span>
                      </label>
                      <select 
                        className={`form-control ${errors.user_type ? 'is-invalid' : ''}`}
                        name="user_type"
                        value={formData.user_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select User Type</option>
                        <option value="client_admin">Client Admin</option>
                        <option value="client_user">Client User</option>
                        <option value="super_user">Super User</option>
                      </select>
                      {errors.user_type && (
                        <div className="invalid-feedback">
                          {errors.user_type}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Organization <span className="text-danger">*</span>
                      </label>
                      <select 
                        className={`form-control ${errors.organization_id ? 'is-invalid' : ''}`}
                        name="organization_id"
                        value={formData.organization_id}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.detail?.org_name || org.detail?.name || `Organization ${org.id}`}
                          </option>
                        ))}
                      </select>
                      {errors.organization_id && (
                        <div className="invalid-feedback">
                          {errors.organization_id}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <div className="input-group">
                        <input 
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Leave blank to auto-generate"
                        />
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={togglePasswordVisibility}
                        >
                          <i className={showPassword ? "ti-eye-off" : "ti-eye"}></i>
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={generatePassword}
                        >
                          <i className="ti-refresh"></i> Generate
                        </button>
                      </div>
                      <small className="form-text text-muted">
                        {generatedPassword ? `Generated: ${generatedPassword}` : 'Leave blank to auto-generate a secure password'}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserModal;
